<template>
  <div
    class="keyframe-thumb"
    :class="{ 'has-keyframe': coverVisible }"
    @click="openDialog"
  >
    <img
      v-if="coverVisible"
      :src="firstKeyframeSrc"
      class="keyframe-img"
      alt="关键帧"
      @error="coverImageError = true"
    />
    <div v-else class="keyframe-placeholder" aria-hidden="true" />

    <!-- hover 播放按钮遮罩 -->
    <div v-if="coverVisible" class="keyframe-play-overlay">
      <i class="el-icon-video-play" />
    </div>

    <!-- 目录解析出的 record_mode 角标 -->
    <span v-if="coverVisible && recordMode" class="record-mode-badge">{{ recordMode }}</span>

    <!-- 关键帧播放弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="860px"
      :close-on-click-modal="false"
      append-to-body
      @open="onDialogOpen"
      @close="onDialogClose"
    >
      <div class="keyframe-dialog-body" v-if="dialogVisible">
        <div class="keyframe-dialog-display">
          <img
            v-if="dialogCurrentSrc && !dialogImageError"
            :src="dialogCurrentSrc"
            class="keyframe-dialog-img"
            alt="关键帧"
            @load="handleDialogImageLoad"
            @error="handleDialogImageError"
          />
          <div v-else class="keyframe-dialog-fallback">
            {{ dialogFallbackText }}
          </div>
          <div class="keyframe-dialog-counter">
            {{ dialogFrameIds.length > 0 ? frameDisplayIndex + 1 : 0 }} / {{ dialogFrameIds.length }}
          </div>
        </div>

        <div class="keyframe-dialog-controls">
          <el-button
            icon="el-icon-d-arrow-left"
            size="small"
            circle
            :disabled="dialogFrameIds.length <= 1"
            @click="prevFrame"
          />
          <el-button
            :icon="dialogPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"
            type="primary"
            size="small"
            circle
            :disabled="dialogFrameIds.length <= 1"
            @click="togglePlay"
          />
          <el-button
            icon="el-icon-d-arrow-right"
            size="small"
            circle
            :disabled="dialogFrameIds.length <= 1"
            @click="nextFrame"
          />
          <span class="keyframe-dialog-speed">
            间隔
            <el-input-number
              v-model="playbackInterval"
              :min="50"
              :max="1000"
              :step="50"
              size="mini"
              style="width:120px"
            /> ms
          </span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchAnnotationKeyframes, getKeyframeImageUrl } from '../api/label';

// 全局缓存：objectName → keyframe IDs
const frameCache = {};

export default {
  name: 'KeyframeThumbnail',

  props: {
    /** 首个前置关键帧 ID（用于默认显示） */
    firstKeyframeId: {
      type: String,
      default: null,
    },
    /** 文件夹名称（用于懒加载关键帧列表） */
    objectName: {
      type: String,
      default: '',
    },
    /** 目录 metadata.json 中的 record_mode 文本，用于封面右下角角标 */
    recordMode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      dialogVisible: false,
      dialogFrameIds: [],
      dialogFrameIndex: 0,
      dialogPlaying: false,
      playbackInterval: 200,
      timer: null,
      coverImageError: false,
      dialogImageLoading: false,
      dialogImageError: false,
      dialogImageReady: false,
      preloadedFrameIds: [],
    };
  },

  computed: {
    firstKeyframeSrc() {
      return this.firstKeyframeId ? getKeyframeImageUrl(this.firstKeyframeId) : null;
    },

    coverVisible() {
      return Boolean(this.firstKeyframeSrc) && !this.coverImageError;
    },

    dialogTitle() {
      return `关键帧 - ${this.objectName || ''}`;
    },

    dialogCurrentSrc() {
      if (this.dialogFrameIds.length === 0) return null;
      const id = this.dialogFrameIds[this.dialogFrameIndex];
      return id ? getKeyframeImageUrl(id) : null;
    },

    dialogFallbackText() {
      if (this.dialogImageError) return '图片加载失败';
      if (this.dialogFrameIds.length === 0) return '暂无关键帧';
      return '加载中...';
    },

    frameDisplayIndex() {
      return this.dialogFrameIndex;
    },
  },

  watch: {
    firstKeyframeId() {
      this.coverImageError = false;
    },
  },

  beforeDestroy() {
    this.clearTimer();
  },

  methods: {
    openDialog() {
      if (!this.firstKeyframeId) return;
      this.dialogVisible = true;
    },

    /** 弹窗打开时加载关键帧列表，加载完成后自动播放 */
    async onDialogOpen() {
      if (!this.objectName || !this.firstKeyframeId) return;

      if (frameCache[this.objectName]) {
        this.dialogFrameIds = frameCache[this.objectName];
        this.resetToCachedStart();
      } else {
        try {
          const res = await fetchAnnotationKeyframes(this.objectName, 'front');
          const ids = (res.data && res.data.ids) ? res.data.ids : [];
          frameCache[this.objectName] = ids;
          this.dialogFrameIds = ids;
          this.resetToCachedStart();
        } catch {
          this.dialogFrameIds = [];
        }
      }

      if (this.dialogFrameIds.length > 1) {
        this.startPlayback();
      }
    },

    /** 将当前帧定位到 firstKeyframeId 对应的位置 */
    resetToCachedStart() {
      const idx = this.dialogFrameIds.indexOf(this.firstKeyframeId);
      this.setDialogFrame(idx >= 0 ? idx : 0);
    },

    onDialogClose() {
      this.stopPlayback();
      this.dialogImageLoading = false;
      this.dialogImageError = false;
      this.dialogImageReady = false;
    },

    togglePlay() {
      if (this.dialogPlaying) {
        this.stopPlayback();
      } else {
        this.startPlayback();
      }
    },

    prevFrame() {
      this.stopPlayback();
      if (this.dialogFrameIds.length <= 1) return;
      const index = this.dialogFrameIndex <= 0
        ? this.dialogFrameIds.length - 1
        : this.dialogFrameIndex - 1;
      this.setDialogFrame(index);
    },

    nextFrame() {
      this.stopPlayback();
      if (this.dialogFrameIds.length <= 1) return;
      const index = (this.dialogFrameIndex + 1) % this.dialogFrameIds.length;
      this.setDialogFrame(index);
    },

    startPlayback() {
      if (this.dialogFrameIds.length <= 1) return;
      this.dialogPlaying = true;
      this.clearTimer();

      if (this.dialogImageReady) {
        this.preloadFrame((this.dialogFrameIndex + 1) % this.dialogFrameIds.length);
        this.scheduleNextFrame();
      } else {
        this.dialogImageLoading = true;
      }
    },

    stopPlayback() {
      this.clearTimer();
      this.dialogPlaying = false;
    },

    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },

    setDialogFrame(index) {
      if (this.dialogFrameIds.length === 0) return;

      this.clearTimer();
      this.dialogFrameIndex = index;
      this.dialogImageLoading = true;
      this.dialogImageError = false;
      this.dialogImageReady = false;

      // 提前预热下一帧
      this.preloadFrame((index + 1) % this.dialogFrameIds.length);
    },

    /** 当前帧加载完成后，才安排下一帧 */
    handleDialogImageLoad() {
      this.dialogImageLoading = false;
      this.dialogImageError = false;
      this.dialogImageReady = true;

      if (!this.dialogPlaying || this.dialogFrameIds.length <= 1) return;

      this.preloadFrame((this.dialogFrameIndex + 1) % this.dialogFrameIds.length);
      this.scheduleNextFrame();
    },

    /** 图片加载失败时显示替代文案并继续播放 */
    handleDialogImageError() {
      this.dialogImageLoading = false;
      this.dialogImageError = true;
      this.dialogImageReady = true;

      if (!this.dialogPlaying || this.dialogFrameIds.length <= 1) return;

      this.preloadFrame((this.dialogFrameIndex + 1) % this.dialogFrameIds.length);
      this.scheduleNextFrame();
    },

    scheduleNextFrame() {
      if (!this.dialogPlaying || this.dialogFrameIds.length <= 1) return;

      this.clearTimer();
      this.timer = setTimeout(() => {
        const next = (this.dialogFrameIndex + 1) % this.dialogFrameIds.length;
        this.setDialogFrame(next);
      }, this.playbackInterval);
    },

    preloadFrame(index) {
      const id = this.dialogFrameIds[index];
      if (!id || this.preloadedFrameIds.includes(id)) return;

      this.preloadedFrameIds.push(id);
      const preloader = new Image();
      preloader.src = getKeyframeImageUrl(id);
    },
  },
};
</script>

<style scoped>
.keyframe-thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  cursor: default;
  position: relative;
  transition: box-shadow 0.2s;
}

.keyframe-thumb.has-keyframe {
  cursor: pointer;
}

.keyframe-thumb.has-keyframe:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.keyframe-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  image-rendering: auto;
}

/* hover 播放按钮遮罩 */
.keyframe-play-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.keyframe-thumb.has-keyframe:hover .keyframe-play-overlay {
  opacity: 1;
}

.keyframe-play-overlay .el-icon-video-play {
  font-size: 22px;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.record-mode-badge {
  position: absolute;
  right: 3px;
  bottom: 3px;
  z-index: 1;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
  border-radius: 2px;
  background: #fff;
  color: #000;
  font-size: 10px;
  line-height: 16px;
  pointer-events: none;
}

.keyframe-placeholder {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    #f0f2f5 0,
    #f0f2f5 8px,
    #e4e7ed 8px,
    #e4e7ed 16px
  );
}

/* 弹窗内样式 */
.keyframe-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.keyframe-dialog-display {
  position: relative;
  width: 100%;
  min-height: 360px;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.keyframe-dialog-img {
  max-width: 100%;
  max-height: 480px;
  object-fit: contain;
  display: block;
}

.keyframe-dialog-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 360px;
  color: #909399;
  font-size: 14px;
  background: #000;
}

.keyframe-dialog-counter {
  position: absolute;
  bottom: 8px;
  right: 12px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.55);
  padding: 2px 10px;
  border-radius: 10px;
  line-height: 1.6;
}

.keyframe-dialog-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.keyframe-dialog-speed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
  margin-left: 8px;
}
</style>
